import ExcelJS from 'exceljs';
import FileSaver from 'file-saver';
import moment from 'moment';
import { IOutcomeResourceShortProps } from '../../../props/finance/outcomes';

export const exportOutcomeInvoicesToExcel = async (invoices: IOutcomeResourceShortProps[], customTitle?: string) => {
    try {
        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Jejak Logistik System';
        workbook.created = new Date();

        const worksheet = workbook.addWorksheet('Outcome Invoices');

        // Title Block
        worksheet.mergeCells('A1:M1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = customTitle || 'LAPORAN OUTCOME INVOICES - JEJAK LOGISTIK';
        titleCell.font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFE48826' } };
        titleCell.alignment = { horizontal: 'left', vertical: 'middle' };

        worksheet.mergeCells('A2:M2');
        const subtitleCell = worksheet.getCell('A2');
        subtitleCell.value = `Tanggal Export: ${moment().format('DD MMMM YYYY, HH:mm')} | Total: ${invoices.length} Invoice(s)`;
        subtitleCell.font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF555555' } };
        subtitleCell.alignment = { horizontal: 'left', vertical: 'middle' };

        // Empty row
        worksheet.addRow([]);

        // Header Row (Row 4)
        const headerTitles = [
            'No',
            'Nama Invoice',
            'Perusahaan (Subsidiary)',
            'Kategori',
            'Jenis Transaksi',
            'Vendor / Target Transfer',
            'Rekening (Transfer From)',
            'Tanggal Jatuh Tempo',
            'Tanggal Dibuat',
            'Total Amount (Rp)',
            'Paid Amount (Rp)',
            'Unpaid Amount (Rp)',
            'Status'
        ];

        const headerRow = worksheet.addRow(headerTitles);
        headerRow.height = 25;

        headerRow.eachCell((cell) => {
            cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE48826' } // Brand Orange/Amber
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = {
                top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
                left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
                bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
                right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
            };
        });

        let grandTotalAmount = 0;
        let grandTotalPaid = 0;
        let grandTotalUnpaid = 0;

        // Data Rows
        invoices.forEach((item, index) => {
            let transactionType = 'Vendor Payment';
            if (item.targetEmployee) { transactionType = 'Transfer to Employee'; }
            else if (item.targetSubsidiary) { transactionType = 'Internal Transfer'; }

            let vendorTarget = '-';
            if (item.vendor) { vendorTarget = item.vendor.name; }
            else if (item.targetSubsidiary) { vendorTarget = item.targetSubsidiary.name; }
            else if (item.targetEmployee) { vendorTarget = item.targetEmployee.name; }

            let transferFrom = '-';
            if (item.bankAccount) {
                const bankName = item.bankAccount.bank?.name || '';
                const accName = item.bankAccount.accountName || '';
                const accNum = item.bankAccount.accountNumber || '';
                transferFrom = [bankName, accName, accNum].filter(Boolean).join(' - ') || '-';
            }

            let statusText = 'Unpaid';
            if (item.status?.toLowerCase() === 'cancelled') {
                statusText = 'Cancelled';
            } else if (Number(item.unpaid) <= 0) {
                statusText = 'Paid';
            }

            const totalAmount = Number(item.totalAmount || 0);
            const unpaidAmount = Number(item.unpaid || 0);
            const paidAmount = totalAmount - unpaidAmount;

            grandTotalAmount += totalAmount;
            grandTotalPaid += paidAmount;
            grandTotalUnpaid += unpaidAmount;

            const rowData = [
                index + 1,
                item.name || '-',
                item.subsidiary?.name || '-',
                item.category?.name || '-',
                transactionType,
                vendorTarget,
                transferFrom,
                item.dueDate ? moment(item.dueDate).format('DD/MM/YYYY') : '-',
                item.createdAt ? moment(item.createdAt).format('DD/MM/YYYY') : '-',
                totalAmount,
                paidAmount,
                unpaidAmount,
                statusText
            ];

            const row = worksheet.addRow(rowData);
            row.height = 20;

            row.eachCell((cell, colNumber) => {
                cell.font = { name: 'Arial', size: 9 };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                    left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                    bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                    right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
                };

                // Alignment & formatting
                if (colNumber === 1 || colNumber === 8 || colNumber === 9 || colNumber === 13) {
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                } else if (colNumber >= 10 && colNumber <= 12) {
                    cell.alignment = { horizontal: 'right', vertical: 'middle' };
                    cell.numFmt = '#,##0';
                } else {
                    cell.alignment = { horizontal: 'left', vertical: 'middle' };
                }
            });
        });

        // Total Summary Row
        const summaryRowIndex = worksheet.rowCount + 1;
        worksheet.mergeCells(`A${summaryRowIndex}:I${summaryRowIndex}`);
        const totalRow = worksheet.getRow(summaryRowIndex);
        totalRow.height = 24;

        const labelCell = worksheet.getCell(`A${summaryRowIndex}`);
        labelCell.value = 'TOTAL';
        labelCell.font = { name: 'Arial', size: 10, bold: true };
        labelCell.alignment = { horizontal: 'right', vertical: 'middle' };

        const totalAmtCell = worksheet.getCell(`J${summaryRowIndex}`);
        totalAmtCell.value = grandTotalAmount;
        totalAmtCell.numFmt = '#,##0';
        totalAmtCell.font = { name: 'Arial', size: 10, bold: true };
        totalAmtCell.alignment = { horizontal: 'right', vertical: 'middle' };

        const paidAmtCell = worksheet.getCell(`K${summaryRowIndex}`);
        paidAmtCell.value = grandTotalPaid;
        paidAmtCell.numFmt = '#,##0';
        paidAmtCell.font = { name: 'Arial', size: 10, bold: true };
        paidAmtCell.alignment = { horizontal: 'right', vertical: 'middle' };

        const unpaidAmtCell = worksheet.getCell(`L${summaryRowIndex}`);
        unpaidAmtCell.value = grandTotalUnpaid;
        unpaidAmtCell.numFmt = '#,##0';
        unpaidAmtCell.font = { name: 'Arial', size: 10, bold: true };
        unpaidAmtCell.alignment = { horizontal: 'right', vertical: 'middle' };

        totalRow.eachCell((cell) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF0F0F0' }
            };
            cell.border = {
                top: { style: 'medium', color: { argb: 'FFAAAAAA' } },
                bottom: { style: 'double', color: { argb: 'FFAAAAAA' } }
            };
        });

        // Set column widths automatically
        worksheet.columns.forEach((column) => {
            let maxLen = 12;
            column.eachCell?.({ includeEmpty: false }, (cell) => {
                const len = cell.value ? cell.value.toString().length : 0;
                if (len > maxLen) { maxLen = len; }
            });
            column.width = Math.min(Math.max(maxLen + 3, 12), 40);
        });

        // Save to File
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const fileName = `Outcome_Invoices_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
        FileSaver.saveAs(blob, fileName);
    } catch (error) {
        console.error('Failed to export outcome invoices to Excel', error);
        throw error;
    }
};
